/**
 * Pagina dettaglio utente completa: dati personali, indirizzo, ruolo,
 * password, gruppi, cartella archivio personale.
 *
 * Visibile solo ad admin e super_admin.
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface GruppoUtente {
  id: string;
  nome: string;
}

interface UtenteData {
  id: string;
  email: string;
  nome: string;
  cognome: string;
  telefono: string | null;
  indirizzo: string | null;
  civico: string | null;
  cap: string | null;
  citta: string | null;
  provincia: string | null;
  data_nascita: string | null;
  codice_fiscale: string | null;
  note: string | null;
  cartella_personale_id: string | null;
  stato: string;
  ruolo: string | null;
  ultimo_accesso: string | null;
  gruppi: GruppoUtente[];
  created_at: string;
  updated_at: string;
}

interface OriginePermesso {
  tipo: 'ruolo' | 'gruppo';
  nome: string;
}

interface PermessoEffettivo {
  codice: string;
  modulo: string;
  azione: string;
  descrizione: string;
  attivo: boolean;
  origini: OriginePermesso[];
}

interface PermessiEffettivi {
  utente_id: string;
  ruolo: string | null;
  accesso_totale: boolean;
  permessi: PermessoEffettivo[];
}

interface FormFields {
  nome: string;
  cognome: string;
  email: string;
  telefono: string;
  indirizzo: string;
  civico: string;
  cap: string;
  citta: string;
  provincia: string;
  data_nascita: string;
  codice_fiscale: string;
  note: string;
}

const UtenteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem('token') ?? '';

  const [utente, setUtente] = useState<UtenteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /* Form modifica */
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<FormFields>({
    nome: '', cognome: '', email: '', telefono: '',
    indirizzo: '', civico: '', cap: '', citta: '',
    provincia: '', data_nascita: '', codice_fiscale: '', note: '',
  });
  const [saving, setSaving] = useState(false);

  /* Ruolo */
  const [ruoloEdit, setRuoloEdit] = useState('');
  const [savingRuolo, setSavingRuolo] = useState(false);

  /* Rami operativi */
  interface UtenteUnitaItem {
    unita_organizzativa_id: string;
    nome: string;
    tipo: string;
    ruolo: string;
  }
  const [unitaUtente, setUnitaUtente] = useState<UtenteUnitaItem[]>([]);
  const [allUnita, setAllUnita] = useState<{ id: string; nome: string; tipo: string }[]>([]);
  const [showAddRamo, setShowAddRamo] = useState(false);
  const [newRamoUoId, setNewRamoUoId] = useState('');
  const [newRamoRuolo, setNewRamoRuolo] = useState('operatore');
  const [savingRamo, setSavingRamo] = useState(false);
  const [ruoliDisponibili, setRuoliDisponibili] = useState<string[]>([]);
  const [statiDisponibili, setStatiDisponibili] = useState<string[]>([]);
  const [savingStato, setSavingStato] = useState(false);

  /* Reset password */
  const [passwordTemp, setPasswordTemp] = useState<string | null>(null);
  const [resettingPw, setResettingPw] = useState(false);

  /* Permessi effettivi */
  const [permessiEffettivi, setPermessiEffettivi] = useState<PermessiEffettivi | null>(null);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/utenti/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: UtenteData = await res.json();
      setUtente(data);
      setFormData({
        nome: data.nome,
        cognome: data.cognome,
        email: data.email,
        telefono: data.telefono ?? '',
        indirizzo: data.indirizzo ?? '',
        civico: data.civico ?? '',
        cap: data.cap ?? '',
        citta: data.citta ?? '',
        provincia: data.provincia ?? '',
        data_nascita: data.data_nascita ?? '',
        codice_fiscale: data.codice_fiscale ?? '',
        note: data.note ?? '',
      });
      setRuoloEdit(data.ruolo ?? '');

      // Carica permessi effettivi
      const permRes = await fetch(`/api/v1/utenti/${id}/permessi-effettivi`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (permRes.ok) {
        setPermessiEffettivi(await permRes.json());
      }

      // Carica rami operativi dell'utente
      const unitaRes = await fetch(`/api/v1/utenti/${id}/unita`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (unitaRes.ok) {
        setUnitaUtente(await unitaRes.json());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  // Carica lista UO e ruoli del tenant
  useEffect(() => {
    const fetchAllUnita = async () => {
      try {
        const res = await fetch('/api/v1/unita', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAllUnita(data.map((u: { id: string; nome: string; tipo: string }) => ({
            id: u.id, nome: u.nome, tipo: u.tipo,
          })));
        }
      } catch { /* ignore */ }
    };
    const fetchLookup = async (url: string, set: (v: string[]) => void) => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) set(await res.json());
      } catch { /* ignore */ }
    };
    void fetchAllUnita();
    void fetchLookup('/api/v1/admin/ruoli', setRuoliDisponibili);
    void fetchLookup('/api/v1/admin/stati-utente', setStatiDisponibili);
  }, [token]);

  useEffect(() => {
    void carica();
  }, [carica]);

  const aggiungiRamo = async () => {
    if (!newRamoUoId || !newRamoRuolo.trim()) return;
    setSavingRamo(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/utenti/${id}/unita`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ unita_organizzativa_id: newRamoUoId, ruolo: newRamoRuolo }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? `Errore ${res.status}`);
      }
      setShowAddRamo(false);
      setNewRamoUoId('');
      setNewRamoRuolo('operatore');
      setSuccess('Ramo assegnato');
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    } finally {
      setSavingRamo(false);
    }
  };

  const rimuoviRamo = async (uoId: string) => {
    setError(null);
    try {
      const res = await fetch(`/api/v1/utenti/${id}/unita/${uoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setSuccess('Ramo rimosso');
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  };

  const salvaModifiche = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(formData)) {
        body[k] = v || null;
      }
      const res = await fetch(`/api/v1/utenti/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setEditMode(false);
      setSuccess('Modifiche salvate');
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  };

  const salvaRuolo = async () => {
    if (!ruoloEdit.trim()) return;
    setSavingRuolo(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/utenti/${id}/ruolo`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ruolo: ruoloEdit.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setSuccess('Ruolo aggiornato');
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSavingRuolo(false);
    }
  };

  const resetPassword = async () => {
    setResettingPw(true);
    setError(null);
    setPasswordTemp(null);
    try {
      const res = await fetch(`/api/v1/utenti/${id}/reset-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data = await res.json();
      setPasswordTemp(data.password_temporanea);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setResettingPw(false);
    }
  };

  const cambiaStato = async (nuovoStato: string) => {
    if (!nuovoStato || nuovoStato === utente?.stato) return;
    setSavingStato(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/utenti/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stato: nuovoStato }),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setSuccess(`Stato aggiornato a "${cap(nuovoStato)}"`);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSavingStato(false);
    }
  };

  const eliminaUtente = async () => {
    if (!confirm('Eliminare questo utente?')) return;
    try {
      const res = await fetch(`/api/v1/utenti/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      navigate('/admin/utenti');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    }
  };

  const fmt = (iso: string | null): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const fmtDate = (iso: string | null): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const setField = (field: keyof FormFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData((p) => ({ ...p, [field]: e.target.value }));

  if (loading) return <p className="p-6 text-center text-sm text-muted-foreground">Caricamento…</p>;
  if (!utente) return <p className="p-6 text-center text-sm text-destructive">Utente non trovato</p>;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="mb-1 text-sm text-muted-foreground hover:underline">
            &larr; Indietro
          </button>
          <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
            {utente.cognome} {utente.nome}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Stato</label>
          <select
            value={utente.stato}
            onChange={(e) => cambiaStato(e.target.value)}
            disabled={savingStato}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ backgroundColor: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)' }}
          >
            {(statiDisponibili.length ? statiDisponibili : [utente.stato]).map((s) => (
              <option key={s} value={s}>{cap(s)}</option>
            ))}
            {statiDisponibili.length > 0 && !statiDisponibili.includes(utente.stato) && (
              <option value={utente.stato}>{cap(utente.stato)}</option>
            )}
          </select>
          <Button variant="destructive" onClick={eliminaUtente}>Elimina</Button>
        </div>
      </div>

      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      {success && <div className="rounded-md bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400">{success}</div>}

      {passwordTemp && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium">Nuova password temporanea:</p>
            <code className="mt-1 block rounded bg-muted px-3 py-2 font-mono text-sm">{passwordTemp}</code>
            <p className="mt-2 text-xs text-muted-foreground">Comunica questa password all'utente.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Dati personali */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Dati personali</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)}>
              {editMode ? 'Annulla' : 'Modifica'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {editMode ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Nome</label>
                    <Input value={formData.nome} onChange={setField('nome')} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Cognome</label>
                    <Input value={formData.cognome} onChange={setField('cognome')} />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Email</label>
                  <Input type="email" value={formData.email} onChange={setField('email')} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Telefono</label>
                    <Input value={formData.telefono} onChange={setField('telefono')} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Codice fiscale</label>
                    <Input value={formData.codice_fiscale} onChange={setField('codice_fiscale')} maxLength={16} />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Data di nascita</label>
                  <Input type="date" value={formData.data_nascita} onChange={setField('data_nascita')} />
                </div>

                <h3 className="pt-2 text-sm font-medium">Indirizzo</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-xs text-muted-foreground">Via / Piazza</label>
                    <Input value={formData.indirizzo} onChange={setField('indirizzo')} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Civico</label>
                    <Input value={formData.civico} onChange={setField('civico')} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">CAP</label>
                    <Input value={formData.cap} onChange={setField('cap')} maxLength={10} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Citta</label>
                    <Input value={formData.citta} onChange={setField('citta')} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Provincia</label>
                    <Input value={formData.provincia} onChange={setField('provincia')} maxLength={5} />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Note</label>
                  <textarea
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    rows={3}
                    value={formData.note}
                    onChange={setField('note')}
                  />
                </div>

                <Button onClick={salvaModifiche} disabled={saving}>
                  {saving ? 'Salvataggio…' : 'Salva'}
                </Button>
              </>
            ) : (
              <div className="space-y-3 text-sm">
                <Row label="Nome" value={utente.nome} />
                <Row label="Cognome" value={utente.cognome} />
                <Row label="Email" value={utente.email} />
                <Row label="Telefono" value={utente.telefono} />
                <Row label="Codice fiscale" value={utente.codice_fiscale} mono />
                <Row label="Data di nascita" value={fmtDate(utente.data_nascita)} />
                <Divider />
                <Row label="Indirizzo" value={
                  [utente.indirizzo, utente.civico].filter(Boolean).join(' ') || null
                } />
                <Row label="CAP / Citta" value={
                  [utente.cap, utente.citta, utente.provincia ? `(${utente.provincia})` : null].filter(Boolean).join(' ') || null
                } />
                <Divider />
                <Row label="Stato" value={
                  <Badge variant={utente.stato === 'attivo' ? 'default' : 'outline'}>
                    {cap(utente.stato)}
                  </Badge>
                } />
                <Row label="Ultimo accesso" value={fmt(utente.ultimo_accesso)} />
                <Row label="Creato il" value={fmt(utente.created_at)} />
                {utente.note && (
                  <>
                    <Divider />
                    <div>
                      <span className="text-muted-foreground">Note</span>
                      <p className="mt-1 whitespace-pre-wrap">{utente.note}</p>
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Colonna destra */}
        <div className="space-y-6">
          {/* Ruolo libero */}
          <Card>
            <CardHeader>
              <CardTitle>Ruolo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <select
                  value={ruoloEdit}
                  onChange={(e) => setRuoloEdit(e.target.value)}
                  className="flex-1 rounded-md border px-3 py-2 text-sm"
                  style={{ backgroundColor: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)' }}
                >
                  {ruoloEdit && !ruoliDisponibili.includes(ruoloEdit) && (
                    <option value={ruoloEdit}>{cap(ruoloEdit)}</option>
                  )}
                  {ruoliDisponibili.map((r) => (
                    <option key={r} value={r}>{cap(r)}</option>
                  ))}
                </select>
                <Button onClick={salvaRuolo} disabled={savingRuolo || !ruoloEdit.trim() || ruoloEdit === (utente.ruolo ?? '')}>
                  {savingRuolo ? '...' : 'Salva'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Ruolo scelto dall'elenco configurato. I ruoli admin e super_admin hanno accesso alla gestione sistema.
              </p>
            </CardContent>
          </Card>

          {/* Rami operativi */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Rami operativi</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddRamo(true)}
                  disabled={showAddRamo}
                >
                  Aggiungi ramo
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {unitaUtente.length === 0 && !showAddRamo && (
                <p className="text-sm text-muted-foreground">
                  Nessun ramo assegnato. L'utente opera a livello tenant senza filtro per ramo.
                </p>
              )}

              {unitaUtente.length > 0 && (
                <div className="space-y-2">
                  {unitaUtente.map((uu) => (
                    <div
                      key={uu.unita_organizzativa_id}
                      className="flex items-center justify-between rounded-md border px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{uu.nome}</span>
                        <Badge variant="secondary">{uu.tipo}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge>{uu.ruolo}</Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => rimuoviRamo(uu.unita_organizzativa_id)}
                          className="text-destructive hover:text-destructive"
                        >
                          Rimuovi
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showAddRamo && (
                <div className="space-y-3 rounded-md border p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-muted-foreground)' }}>Ramo</label>
                      <select
                        value={newRamoUoId}
                        onChange={(e) => setNewRamoUoId(e.target.value)}
                        className="w-full rounded-md border px-3 py-2 text-sm"
                        style={{ backgroundColor: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)' }}
                      >
                        <option value="">Seleziona...</option>
                        {allUnita
                          .filter((u) => !unitaUtente.some((uu) => uu.unita_organizzativa_id === u.id))
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.nome} ({u.tipo})
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-muted-foreground)' }}>Ruolo</label>
                      <select
                        value={newRamoRuolo}
                        onChange={(e) => setNewRamoRuolo(e.target.value)}
                        className="w-full rounded-md border px-3 py-2 text-sm"
                        style={{ backgroundColor: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)' }}
                      >
                        {ruoliDisponibili.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={aggiungiRamo} disabled={savingRamo || !newRamoUoId}>
                      {savingRamo ? '...' : 'Salva'}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowAddRamo(false)}>
                      Annulla
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sicurezza */}
          <Card>
            <CardHeader>
              <CardTitle>Sicurezza</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={resetPassword} disabled={resettingPw}>
                {resettingPw ? 'Reset in corso…' : 'Reset password'}
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                Genera una nuova password temporanea per l'utente.
              </p>
            </CardContent>
          </Card>

          {/* Gruppi */}
          <Card>
            <CardHeader>
              <CardTitle>Gruppi</CardTitle>
            </CardHeader>
            <CardContent>
              {utente.gruppi.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessun gruppo assegnato</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {utente.gruppi.map((g) => (
                    <Badge key={g.id} variant="secondary">{g.nome}</Badge>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                I gruppi si gestiscono dalla{' '}
                <button onClick={() => navigate('/admin/gruppi')} className="underline">pagina Gruppi</button>.
              </p>
            </CardContent>
          </Card>

          {/* Archivio personale */}
          <Card>
            <CardHeader>
              <CardTitle>Archivio personale</CardTitle>
            </CardHeader>
            <CardContent>
              {utente.cartella_personale_id ? (
                <>
                  <p className="text-sm">
                    Cartella personale collegata nell'archivio documentale.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => navigate('/documentale/archivio')}
                  >
                    Apri archivio
                  </Button>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Struttura: Personale / {utente.cognome} {utente.nome} / (Documenti personali, Buste paga, Contratti)
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nessuna cartella personale. Verra creata automaticamente alla prossima modifica
                  o se il modulo documentale e attivo.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Permessi effettivi — full width sotto le due colonne */}
      {permessiEffettivi && (
        <Card>
          <CardHeader>
            <CardTitle>Permessi effettivi</CardTitle>
          </CardHeader>
          <CardContent>
            {permessiEffettivi.accesso_totale ? (
              <div className="rounded-md border border-primary/20 bg-primary/5 p-4">
                <p className="text-sm font-medium">
                  Accesso totale — il ruolo <Badge variant="default">{permessiEffettivi.ruolo}</Badge> ha tutti i permessi su questo tenant.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const moduli = [...new Set(permessiEffettivi.permessi.map((p) => p.modulo))];
                  const moduloLabels: Record<string, string> = {
                    core: 'Sistema', documentale: 'Documentale', fornitori: 'Fornitori',
                    magazzino: 'Magazzino', cassa: 'Cassa', ordini: 'Ordini', prezzi: 'Prezzi',
                  };
                  return moduli.map((modulo) => {
                    const permsModulo = permessiEffettivi.permessi.filter((p) => p.modulo === modulo);
                    const attiviCount = permsModulo.filter((p) => p.attivo).length;
                    return (
                      <div key={modulo}>
                        <div className="mb-2 flex items-center gap-2">
                          <h3 className="text-sm font-semibold">{moduloLabels[modulo] ?? modulo}</h3>
                          <span className="text-xs text-muted-foreground">
                            {attiviCount}/{permsModulo.length}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                          {permsModulo.map((perm) => (
                            <div
                              key={perm.codice}
                              className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                                perm.attivo
                                  ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950'
                                  : 'border-border bg-muted/30 text-muted-foreground'
                              }`}
                            >
                              <span className="mt-0.5 flex-shrink-0">
                                {perm.attivo ? '✓' : '—'}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className={`font-medium ${perm.attivo ? '' : 'text-muted-foreground'}`}>
                                  {perm.descrizione}
                                </p>
                                {perm.attivo && perm.origini.length > 0 && (
                                  <p className="mt-0.5 text-xs text-muted-foreground">
                                    {perm.origini.map((o) =>
                                      o.tipo === 'ruolo' ? `Ruolo: ${o.nome}` : `Gruppo: ${o.nome}`
                                    ).join(' · ')}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
                {permessiEffettivi.permessi.every((p) => !p.attivo) && (
                  <p className="text-sm text-muted-foreground">
                    Nessun permesso assegnato. Cambia il ruolo o aggiungi l'utente a un gruppo con permessi.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/** Capitalizza la prima lettera (per gli stati/ruoli nei dropdown). */
const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Riga label-valore per la vista read-only. */
const Row: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={mono ? 'font-mono' : ''}>{value ?? '—'}</span>
  </div>
);

/** Separatore sottile. */
const Divider: React.FC = () => (
  <hr className="border-t border-border" />
);

export default UtenteDetail;
